import React, { useEffect, useState } from "react";
import { FaEye, FaFileWord, FaFilePdf, FaFileCsv, FaFileAlt, FaRegTrashAlt } from "react-icons/fa";
import DragAndDropDocument from "../../../../../../components/DocumentUpload/upload";
import moment from "moment";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const DocumentsComponent = ({ items, mediaData, handleMediaDelete, handleCreateMedia }) => {
  const [uploadedDocument, setUploadedDocument] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    setUploadedDocument(mediaData || []);
  }, [mediaData]);

  const handleDocumentsUpdate = (images) => {
    setUploadedDocument(images);
    handleCreateMedia(images);
  };

  const handleCheckboxChange = (file) => {
    setSelectedFiles((prevSelected) =>
      prevSelected.includes(file)
        ? prevSelected.filter((item) => item !== file)
        : [...prevSelected, file]
    );
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filteredMediaData);
    }
    setSelectAll(!selectAll);
  };

  // Modified download handler:
  const handleDownloadSelected = async () => {
    if (selectedFiles.length === 0) return;

    // Single file download: download file directly
    if (selectedFiles.length === 1) {
      const doc = selectedFiles[0];
      try {
        const response = await fetch(doc.url);
        const blob = await response.blob();
        saveAs(blob, doc.name || "document");
      } catch (error) {
        console.error("Error downloading file:", error);
        alert("Error downloading file.");
      }
      return;
    }

    // Multiple files: download as a zip archive
    const zip = new JSZip();
    const folder = zip.folder("Selected_Documents");

    try {
      await Promise.all(
        selectedFiles.map(async (doc) => {
          if (doc.url) {
            const response = await fetch(doc.url);
            const blob = await response.blob();
            folder.file(doc.name || "document", blob);
          }
        })
      );
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "Selected_Documents.zip");
    } catch (error) {
      console.error("Error downloading files:", error);
      alert("Error downloading files.");
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    if (ext === "docx" || ext === "doc") return <FaFileWord className="text-blue-500 text-xl" />;
    if (ext === "pdf") return <FaFilePdf className="text-red-500 text-xl" />;
    if (ext === "csv") return <FaFileCsv className="text-green-500 text-xl" />;
    return <FaFileAlt className="text-gray-500 text-xl" />;
  };

  const filteredMediaData = mediaData?.filter((doc) => {
    const ext = doc.name?.split(".").pop().toLowerCase();
    return ["docx", "doc", "pdf", "csv"].includes(ext);
  });

  useEffect(() => {
    setSelectedFiles(prev =>
      prev.filter(file => filteredMediaData.some(doc => doc.id === file.id))
    );
  }, [filteredMediaData]);

  useEffect(() => {
    setSelectAll(selectedFiles.length === filteredMediaData.length && filteredMediaData.length > 0);
  }, [selectedFiles, filteredMediaData]);

  return (
    <div className="p-6">
      <div className="flex mb-4 gap-16 items-center">
        <DragAndDropDocument
          onImagesUpdate={handleDocumentsUpdate}
          handleMediaDelete={handleMediaDelete}
          uploadedImages={uploadedDocument}
          accept=".csv,.docx,.doc,.pdf"
          showPic={3}
        />
        <button
          onClick={handleDownloadSelected}
          disabled={selectedFiles.length === 0}
          className={`cursor-pointer px-4 py-[7px] rounded-md ${selectedFiles.length > 0
            ? "bg-secondary text-primary"
            : "bg-gray-400 text-gray-700 opacity-50 cursor-not-allowed"
            }`}
        >
          Download
        </button>
      </div>

      <table className="w-full border-collapse border-t border-transparent text-left">
        <thead>
          <tr className="border-b border-white">
            {["", "File", "Related Item", "Preview", "Date", "Action"].map((header, index) => (
              <th key={index} className="py-2">
                {index === 0 ? (
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAllChange}
                    className="cursor-pointer"
                  />
                ) : (
                  header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredMediaData?.length > 0 ? (
            filteredMediaData.map((doc, index) => (
              <tr key={index} className="border-b border-transparent">
                <td className="py-2">
                  <input
                    type="checkbox"
                    checked={selectedFiles.includes(doc)}
                    onChange={() => handleCheckboxChange(doc)}
                    className="cursor-pointer"
                  />
                </td>
                <td className="py-2 text-secondary flex items-center gap-2">
                  {getFileIcon(doc.name)}
                  {doc.name ? (
                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="underline">
                      {doc.name}
                    </a>
                  ) : (
                    "N/A"
                  )}
                </td>
                <td className="py-2 text-secondary">{items?.name || "N/A"}</td>
                <td className="py-2 text-center">
                  {doc.url ? (
                    <a href={doc.url} target="_blank" rel="noopener noreferrer">
                      <FaEye className="text-white cursor-pointer" />
                    </a>
                  ) : (
                    "N/A"
                  )}
                </td>
                <td className="py-2">
                  {doc.created_at ? moment(doc.created_at).format("MM/DD/YYYY") : "N/A"}
                </td>
                <td className="py-2 text-center">
                  <FaRegTrashAlt
                    className="text-expense cursor-pointer hover:text-red-700"
                    onClick={() => {handleMediaDelete(doc.id, "File")}}
                  />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="py-4 text-center text-gray-500">
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DocumentsComponent;
